import React from "react";
import { Box } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { motion } from "framer-motion";

// Each digit's row height, in em of the surrounding text (rounded to whole
// device pixels)
const ROW = 1.2;
const DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

/**
 * One digit as a 0–9 column behind a one-row window, rolled to show `digit`.
 * Arriving after the number first rendered, it rolls up from 0.
 *
 * Rows are a whole number of device pixels tall: at a fractional height (1.2em
 * of 14px is 16.8px) the browser snaps each row's text to the pixel grid but
 * not the column's offset, so digits came to rest up to half a pixel askew of
 * one another, by how far down their column they sat. Whole CSS pixels aren't
 * enough on Android, whose fractional devicePixelRatio (e.g. 2.625) makes a
 * 17px row 44.625 device pixels.
 */
const Digit = ({ digit, rollIn, rowHeight }) => (
  <Box
    component="span"
    aria-hidden
    sx={{ display: "inline-block", height: rowHeight, overflow: "hidden" }}
  >
    <motion.span
      style={{ display: "flex", flexDirection: "column" }}
      initial={rollIn ? { y: 0 } : false}
      animate={{ y: -digit * rowHeight }}
      transition={{ type: "spring", stiffness: 260, damping: 28 }}
    >
      {DIGITS.map((d) => (
        <span
          key={d}
          style={{ height: rowHeight, lineHeight: `${rowHeight}px` }}
        >
          {d}
        </span>
      ))}
    </motion.span>
  </Box>
);

/**
 * A number (or formatted string, e.g. "20.7k" or "3.8") whose digits roll to
 * each new value like an odometer; other characters change in place. Digits
 * are matched by place from the right, so 68 → 64 rolls only the units, and
 * 68 → 112 keeps the units' column and adds a hundreds digit. Shows its first
 * value still; only later changes roll.
 *
 * Tabular figures keep every digit the same width, so nothing shifts sideways
 * mid-roll.
 */
export default function RollingNumber({ value }) {
  const text = String(value);
  const ref = React.useRef(null);
  // Whole-device-pixel row height from the inherited font size (see Digit);
  // null until measured, before the first paint
  const [rowHeight, setRowHeight] = React.useState(null);
  // A digit column added by a later value rolls in; the first value's don't
  const hasRenderedRef = React.useRef(false);

  React.useLayoutEffect(() => {
    const fontSize = parseFloat(getComputedStyle(ref.current).fontSize);
    const dpr = window.devicePixelRatio || 1;
    setRowHeight(Math.round(fontSize * ROW * dpr) / dpr);
  }, []);

  // Only once the digit columns exist (after measuring), or the first
  // value's would count as later arrivals and roll in
  React.useEffect(() => {
    if (rowHeight) hasRenderedRef.current = true;
  }, [rowHeight]);

  return (
    <Box
      ref={ref}
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {/* Screen readers get the value as text, not ten digits per column */}
      <Box component="span" sx={visuallyHidden}>
        {text}
      </Box>
      {[...text].map((char, index) => {
        const place = text.length - index;
        const isDigit = char >= "0" && char <= "9";
        return isDigit && rowHeight ? (
          <Digit
            key={`d${place}`}
            digit={Number(char)}
            rollIn={hasRenderedRef.current}
            rowHeight={rowHeight}
          />
        ) : (
          <span key={`c${place}${char}`} aria-hidden>
            {char}
          </span>
        );
      })}
    </Box>
  );
}
